import { Grid } from "@mui/material";
import React, { useState } from "react";
import { FC } from "react";
import { TopLevelPaper } from "../../../../../../themes/StyledComponents";
import useStore from "../../../../../../helpers/useStore";
import BillBoxWrapper from "../../../DirectBilling/common/components/BillBoxWrapper";
import { observer } from "mobx-react";
import TransactionRxOverview from "../../../Base/components/TransactionRxOverview";
import { BillingTypeEnum, ScreenMode } from "../../../Base/types/insuranceBills.types";
import { ExportBillingData } from "../../../../../../api-client";
import { TransactionsContext } from "../../../../../../stores/Transactions/Transactions.provider";
import EditFullScreen from "../../../DirectBilling/Edit/pages/EditFullScreen";
import CorrectionBillInfo from "../components/CorrectionBillInfo";
import { EditCorrectionBillFullScreen } from "../../Edit/exports";
import { useMasterTables } from "../../../../../../stores/MasterTables";

interface NewCorrectionBillProps {
  handleFullScreenDialogClose: () => void;
  selectedAccount: string;
  userName: string;
  userId: string;
}

const NewCorrectionBill: FC<NewCorrectionBillProps> = observer(
  ({ handleFullScreenDialogClose, selectedAccount, userName, userId }) => {
    /**
     * Setting a mode to determine if we are in "new" or "edit" mode and a state to hold the bills that are being edited.
     * If we are in "new" mode, we will show the CorrectionBillInfo component.
     * If we are in "edit" mode, we will show the EditFullScreen component.
     * When the user saves the bills, we will switch back to "edit" mode and update the editBills state with the newly created bills.
     * This allows us to edit the bills that were just created.
     * If the user closes the editor, we will switch back to "new" mode and clear the editBills state.
     */
    const [mode, setMode] = useState<ScreenMode>(ScreenMode.NEW);
    const [editBills, setEditBills] = useState<ExportBillingData[]>([]);

    const [selectedRx, setSelectedRx] = useState<any>(null); // Add state to store selected rx
    const [isVisible, setIsVisible] = useState(false);
    const { data: masterTablesData } = useMasterTables();
    const [editRxMode, setEditRxMode] = useState<boolean>(false);
    const transactionStore = useStore(TransactionsContext);
    const { getBillingDataForEditing } = transactionStore;

    const handleRxDelete = (deletedRxId: string) => {
      if (selectedRx && selectedRx.id === deletedRxId) {
        setSelectedRx(null);
        setIsVisible(false);
      }
    };

    const handleRowClick = (rx: any) => {
      setSelectedRx(rx); // Update the selected rx when a row is clicked
      setIsVisible(true);
    };

    const handleValidationFilter = (remainingRxs: any[]) => {
      // Check if the selected RX is no longer in the list of remaining RXs
      if (selectedRx && !remainingRxs.some((rx) => rx.id === selectedRx.id)) {
        setSelectedRx(null);
        setIsVisible(false);
      }
    };

    /**
     * This function is called when the user saves the bills.
     * It fetches the billing data for editing and sets the editBills state with the fetched data.
     * It also switches the mode to "edit" to show the EditFullScreen component.
     */
    const handleBillsSaved = async (ids: string[]) => {
      const bills = await getBillingDataForEditing(ids, BillingTypeEnum.CORRECTION);
      setEditBills(bills);
      setMode(ScreenMode.EDIT);
    };

    /* ---------  render  --------- */

    /**
     * If we are in "edit" mode, we show the EditFullScreen component.
     * This component will handle the editing of the bills and will call the handleFullScreenDialogClose function
     * when the user closes the editor.
     * When the editor is closed, we reset the mode to "new" and clear the editBills state.
     * This allows us to return to the "new" mode and start creating new bills again.
     */
    if (mode === ScreenMode.EDIT) {
      return (
        <EditCorrectionBillFullScreen
          handleFullScreenDialogClose={() => {
            setMode(ScreenMode.NEW);
            setEditBills([]);
            handleFullScreenDialogClose?.();
          }}
          initialBills={editBills}
        />
      );
    }

    return (
      <Grid
        container
        spacing={1}
        sx={{ height: "100%", marginTop: "1px" }}
      >
        <BillBoxWrapper xs={5}>
          <TopLevelPaper
            className="scrollable"
            sx={{ paddingTop: 0 }}
          >
            {/* Pass handleRowClick to CorrectionBillInfo */}
            <CorrectionBillInfo
              handleFullScreenDialogClose={handleFullScreenDialogClose}
              onRowClick={handleRowClick}
              account={selectedAccount}
              userName={userName}
              userId={userId}
              onRxDelete={handleRxDelete}
              onValidationFilter={handleValidationFilter}
              onBillsSaved={handleBillsSaved}
              selectedRx={selectedRx}
            />
          </TopLevelPaper>
        </BillBoxWrapper>
        {isVisible && (
          <>
            <BillBoxWrapper xs={7}>
              <TopLevelPaper className="scrollable rounded-container">
                {/* Pass the selected rx to TransactionRxOverview */}
                <TransactionRxOverview
                  rowData={selectedRx}
                  masterData={masterTablesData}
                  editRxMode={editRxMode}
                  setEditRxMode={setEditRxMode}
                />
              </TopLevelPaper>
            </BillBoxWrapper>
          </>
        )}
      </Grid>
    );
  }
);

export default NewCorrectionBill;
